/**
 * @file Mi perfil público — restyled to match design page-perfil-publico.jsx.
 *
 * Layout:
 *  - pg-head with title + actions (Vista previa + Guardar)
 *  - 2-col grid: profile-card (form) + share-card (link, QR, share channels)
 *
 * Configuración (accounts, services, locations, specialties, schedule)
 * lives at /configuracion. Schedule manager stays at /mi-horario.
 */
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, Share2, Check, ExternalLink, MessageCircle } from "lucide-react";
import { useProfessionalProfile } from "../hooks/useProfessionals";
import { professionalService } from "../services/professional";
import { PROFESSIONAL_TITLES } from "../constants/strings";
import { useAuth } from "../hooks/useAuth";
import {
  PROFILE_COLORS,
  PROFILE_COLOR_KEYS,
  DEFAULT_PROFILE_COLOR,
} from "../constants/profileColors";
import { Spinner, toast } from "../ui";

const SLUG_REGEX = /^[a-z0-9-]+$/;

const profileSchema = Yup.object({
  title: Yup.string().nullable(),
  firstName: Yup.string().required("Requerido"),
  lastName: Yup.string().required("Requerido"),
  bio: Yup.string().max(1000, "Máximo 1000 caracteres"),
  publicEmail: Yup.string().email("Email inválido").nullable(),
  publicPhone: Yup.string().nullable(),
  slug: Yup.string()
    .matches(SLUG_REGEX, "Solo minúsculas, números y guiones")
    .min(3, "Mínimo 3")
    .max(40, "Máximo 40")
    .required("Requerido"),
});

const initials = (first = "", last = "") =>
  `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";

export default function MyProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, refresh, isLoading } = useProfessionalProfile(true);
  const [colorKey, setColorKey] = useState(profile?.profileColor || DEFAULT_PROFILE_COLOR);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);
  const qrRef = useRef(null);

  React.useEffect(() => {
    if (profile?.profileColor) setColorKey(profile.profileColor);
  }, [profile?.profileColor]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: profile?.title || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      bio: profile?.bio || "",
      publicEmail: profile?.publicEmail || "",
      publicPhone: profile?.publicPhone || "",
      slug: profile?.slug || "",
    },
    validationSchema: profileSchema,
    onSubmit: async (values) => {
      try {
        const tasks = [
          professionalService.updateMe({
            title: values.title || null,
            firstName: values.firstName,
            lastName: values.lastName,
            bio: values.bio,
            publicEmail: values.publicEmail,
            publicPhone: values.publicPhone,
            profileColor: colorKey,
          }),
        ];
        if (values.slug && values.slug !== profile?.slug) {
          tasks.push(professionalService.updateSlug(values.slug));
        }
        await Promise.all(tasks);
        toast.success("Perfil actualizado");
        refresh();
      } catch (err) {
        toast.error(err?.response?.data?.message || "No se pudo guardar");
      }
    },
  });

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      await professionalService.uploadPhoto(fd);
      toast.success("Foto actualizada");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "No se pudo subir la foto");
    } finally {
      setPhotoUploading(false);
    }
  };

  const profileUrl = formik.values.slug ? `consultto.mx/c/${formik.values.slug}` : "";
  const fullProfileUrl = formik.values.slug ? `https://consultto.mx/c/${formik.values.slug}` : "";

  const handleCopy = async () => {
    if (!fullProfileUrl) return;
    try {
      await navigator.clipboard.writeText(fullProfileUrl);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const handleDownloadQR = () => {
    const node = qrRef.current?.querySelector("canvas");
    if (!node) return;
    const url = node.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `consultto-${formik.values.slug || "perfil"}.png`;
    a.click();
  };

  const handleShare = (platform) => {
    if (!fullProfileUrl) return;
    const text = encodeURIComponent("Agenda tu cita conmigo:");
    const url = encodeURIComponent(fullProfileUrl);
    const links = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      instagram: fullProfileUrl,
    };
    const target = links[platform];
    if (platform === "instagram") {
      navigator.clipboard.writeText(fullProfileUrl);
      toast.info("Enlace copiado, pégalo en tu bio de Instagram");
    } else if (target) {
      window.open(target, "_blank", "noopener,noreferrer");
    }
  };

  if (isLoading || !profile) {
    return (
      <div style={{ padding: 60, display: "grid", placeItems: "center" }}>
        <Spinner />
      </div>
    );
  }

  const colorMain = PROFILE_COLORS[colorKey]?.main || PROFILE_COLORS[DEFAULT_PROFILE_COLOR].main;

  return (
    <>
      <div className="pg-head">
          <div>
            <div className="cd-eyebrow">Editar mi perfil</div>
            <h1 className="pg-title">Perfil <em>público</em></h1>
            <p className="pg-sub">
              Así te ven los pacientes cuando agendan contigo. Mantén tu información actualizada
              para inspirar confianza.
            </p>
          </div>
          <div className="pg-actions">
            {fullProfileUrl && (
              <a href={fullProfileUrl} target="_blank" rel="noreferrer" className="pg-btn">
                <ExternalLink size={14} /> Vista previa
              </a>
            )}
            <button
              type="button"
              className="pg-btn solid"
              onClick={async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                  formik.setTouched(
                    Object.keys(errors).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
                    false
                  );
                  toast.error("Revisa los campos resaltados");
                  return;
                }
                formik.submitForm();
              }}
              disabled={formik.isSubmitting}
            >
              <Check size={14} /> {formik.isSubmitting ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </div>

        <div className="perfil-cols" style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 24 }}>
          {/* LEFT: profile-card */}
          <div>
            <form onSubmit={formik.handleSubmit} className="profile-card">
              <div className="profile-hero">
                <button
                  type="button"
                  className="profile-avatar-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoUploading}
                  title="Cambiar foto de perfil"
                  aria-label="Cambiar foto de perfil"
                >
                  <div
                    className="profile-avatar"
                    style={{ background: `linear-gradient(140deg, ${colorMain}, ${colorMain}cc)` }}
                  >
                    {profile.photoUrl ? (
                      <img src={profile.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      initials(formik.values.firstName, formik.values.lastName)
                    )}
                    <div className="profile-avatar-overlay">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                      </svg>
                      <span>{photoUploading ? "Subiendo…" : "Cambiar foto"}</span>
                    </div>
                  </div>
                  <div className="profile-avatar-badge">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  </div>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handlePhoto}
                />
                <div className="info">
                  <h2>
                    {[formik.values.title, formik.values.firstName, formik.values.lastName].filter(Boolean).join(" ")}
                  </h2>
                  <div className="specialty">{formik.values.specialty || "Tu especialidad"}</div>
                  {profileUrl && <span className="url"><ExternalLink size={12} /> {profileUrl}</span>}
                </div>
              </div>

              <div className="form-row cols-3">
                <div className="form-field">
                  <label>Título profesional</label>
                  <select
                    className="form-select"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                  >
                    <option value="">Sin título</option>
                    {PROFESSIONAL_TITLES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Nombre *</label>
                  <input
                    className="form-input"
                    name="firstName"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.firstName && formik.errors.firstName && (
                    <div className="help" style={{ color: "var(--err)" }}>{formik.errors.firstName}</div>
                  )}
                </div>
                <div className="form-field">
                  <label>Apellidos *</label>
                  <input
                    className="form-input"
                    name="lastName"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.lastName && formik.errors.lastName && (
                    <div className="help" style={{ color: "var(--err)" }}>{formik.errors.lastName}</div>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label>Bio profesional</label>
                <textarea
                  className="form-textarea"
                  name="bio"
                  rows={3}
                  value={formik.values.bio}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className="help">
                  {formik.values.bio.length} / 1000 caracteres · descripción que verán los pacientes.
                </div>
                {formik.touched.bio && formik.errors.bio && (
                  <div className="help" style={{ color: "var(--err)" }}>{formik.errors.bio}</div>
                )}
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--ink-2)", marginBottom: 4 }}>
                  Datos de contacto públicos
                </label>
                <div className="help" style={{ marginBottom: 10 }}>
                  Esto se mostrará en tu perfil para que los pacientes puedan contactarte directamente.
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Teléfono público</label>
                  <input
                    className="form-input"
                    name="publicPhone"
                    value={formik.values.publicPhone}
                    onChange={formik.handleChange}
                    placeholder="+52 55 1234 5678"
                  />
                </div>
                <div className="form-field">
                  <label>Email público</label>
                  <input
                    className="form-input"
                    name="publicEmail"
                    value={formik.values.publicEmail}
                    onChange={formik.handleChange}
                    placeholder="contacto@tuconsulta.com"
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Slug del perfil *</label>
                <div style={{ display: "flex", alignItems: "stretch" }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", padding: "0 12px",
                    background: "var(--bg-tint)", border: "1px solid var(--line)",
                    borderRight: 0, borderRadius: "10px 0 0 10px",
                    fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-3)",
                  }}>
                    consultto.mx/c/
                  </span>
                  <input
                    className="form-input"
                    style={{ borderRadius: "0 10px 10px 0", flex: 1 }}
                    name="slug"
                    value={formik.values.slug}
                    onChange={formik.handleChange}
                  />
                </div>
                {formik.touched.slug && formik.errors.slug && (
                  <div className="help" style={{ color: "var(--err)" }}>{formik.errors.slug}</div>
                )}
              </div>

              <div className="form-field" style={{ marginBottom: 4 }}>
                <label>Color de perfil</label>
                <div className="help" style={{ marginBottom: 12 }}>
                  Acento visible en tu perfil público y enlace QR.
                </div>
                <div className="color-swatches">
                  {PROFILE_COLOR_KEYS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setColorKey(k)}
                      className={k === colorKey ? "active" : ""}
                      style={{ background: PROFILE_COLORS[k].main }}
                      aria-label={"Color " + PROFILE_COLORS[k].label}
                      title={PROFILE_COLORS[k].label}
                    />
                  ))}
                </div>
              </div>
            </form>
          </div>

          {/* RIGHT: share-card */}
          <div>
            <div className="share-card">
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <Share2 size={14} />
                <div style={{
                  fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.1em",
                  textTransform: "uppercase", color: "var(--accent-deep)", fontWeight: 600,
                }}>
                  Tu enlace de citas
                </div>
              </div>
              <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "0 0 14px" }}>
                Compártelo con tus pacientes para que agenden contigo en segundos.
              </p>
              <div className="share-link">
                <span className="url">{fullProfileUrl || "Configura tu slug primero"}</span>
                <button type="button" onClick={handleCopy} disabled={!fullProfileUrl}>
                  <Copy size={13} /> {copied ? "Copiado" : "Copiar"}
                </button>
              </div>
              {fullProfileUrl && (
                <div className="qr-block">
                  <div className="qr-img" ref={qrRef} style={{ background: "#fff", padding: 8, borderRadius: 6 }}>
                    <QRCodeCanvas value={fullProfileUrl} size={110} fgColor="#000000" />
                  </div>
                  <div className="qr-actions">
                    <h5>Código QR</h5>
                    <p>Imprímelo en tarjetas de presentación o en tu consultorio.</p>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        className="pg-btn"
                        style={{ flex: 1, justifyContent: "center" }}
                        onClick={handleDownloadQR}
                      >
                        <Download size={13} /> PNG
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {fullProfileUrl && (
                <>
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 8, fontWeight: 500 }}>
                    Compartir vía
                  </div>
                  <div className="share-channels">
                    <button type="button" onClick={() => handleShare("whatsapp")}>
                      <span className="dot" style={{ background: "#25D366" }} /> WhatsApp
                    </button>
                    <button type="button" onClick={() => handleShare("facebook")}>
                      <span className="dot" style={{ background: "#1877F2" }} /> Facebook
                    </button>
                    <button type="button" onClick={() => handleShare("twitter")}>
                      <span className="dot" style={{ background: "#000" }} /> X / Twitter
                    </button>
                    <button type="button" onClick={() => handleShare("instagram")}>
                      <span className="dot" style={{ background: "#E4405F" }} /> Instagram
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

      <style>{`
        @media (max-width: 980px) { .perfil-cols { grid-template-columns: 1fr !important; } }
      `}</style>
    </>
  );
}
